import { ADJUSTMENTS, defaultAdjustmentState } from '../adjustments';
import { BUILTIN_GRADIENTS } from '../core/gradient';
import { readLayerEffects } from '../core/layerStyles';
import { allPatterns } from '../core/patterns';
import { cloneBlendIf } from '../core/blendIf';
import { CHANNEL_OVERLAY_COLOR, DEFAULT_BLEND_CHANNELS, DEFAULT_BLEND_IF } from '../core/types';
import type {
  AdjustmentState,
  AlphaChannel,
  BlendChannels,
  BlendMode,
  ColorChannelVisibility,
  FillContent,
  Layer,
  RasterLayer,
  SmartFilter,
} from '../core/types';
import type { Guide } from '../core/guides';
import type { VectorPath } from '../vector/types';
import { FILTERS, type FilterId } from '../filters';

const DB_NAME = 'pixotah';
/** IndexedDB layout version; bump only when object stores or indexes change. */
const DB_VERSION = 1;
const STORE = 'documents';

/** Per-record metadata schema; independent of `DB_VERSION`. */
export const PERSIST_SCHEMA_VERSION = 4 as const;

export interface StoredSurface {
  /** Straight-alpha RGBA, rows top-to-bottom, deflate-compressed when possible. */
  pixels: ArrayBuffer;
  /** Single-channel layer mask, same encoding as `pixels`. */
  mask: ArrayBuffer | null;
  /** Unfiltered smart-object original, when the layer is smart. */
  smartPixels?: ArrayBuffer | null;
}

export interface StoredDocument {
  id: string;
  name: string;
  width: number;
  height: number;
  activeLayerId: string | null;
  savedAt: number;
  /** False when the browser lacks `CompressionStream` and bytes are raw. */
  compressed: boolean;
  schemaVersion: typeof PERSIST_SCHEMA_VERSION;
  /** Nested layer metadata; index 0 is the bottom-most root layer. */
  layers: Layer[];
  /** Pixel payloads keyed by surface layer id (see `isSurfaceLayer`). */
  surfaces: Record<string, StoredSurface>;
  /** Alpha channels, including Quick Mask while the mode is on. */
  channels: AlphaChannel[];
  /** Single-channel payloads keyed by channel id, same encoding as `pixels`. */
  channelData: Record<string, ArrayBuffer>;
  colorVisible: ColorChannelVisibility;
  /** Saved vector paths, the rows of the Paths panel. */
  paths: VectorPath[];
  activePathId: string | null;
  guides: Guide[];
}

/** v1 on-disk shape: flat list with inline pixel payloads. */
interface StoredLayerV1 {
  meta: Record<string, unknown>;
  pixels: ArrayBuffer;
  mask: ArrayBuffer | null;
}

interface StoredDocumentV1 {
  id: string;
  name: string;
  width: number;
  height: number;
  activeLayerId: string | null;
  savedAt: number;
  compressed: boolean;
  schemaVersion?: number;
  layers: StoredLayerV1[];
}

const BLEND_MODES = new Set<BlendMode>([
  'normal',
  'multiply',
  'screen',
  'overlay',
  'darken',
  'lighten',
  'colorDodge',
  'colorBurn',
  'hardLight',
  'softLight',
  'difference',
  'exclusion',
  'hue',
  'saturation',
  'color',
  'luminosity',
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function readString(value: unknown, fallback: string): string {
  return typeof value === 'string' ? value : fallback;
}

function readNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function readBlendMode(value: unknown): BlendMode {
  return typeof value === 'string' && BLEND_MODES.has(value as BlendMode)
    ? (value as BlendMode)
    : 'normal';
}

function looksLikeV1Layers(layers: unknown): layers is StoredLayerV1[] {
  if (!Array.isArray(layers) || layers.length === 0) return false;
  const first = layers[0];
  return isRecord(first) && 'meta' in first && 'pixels' in first;
}

function looksLikeV2Tree(layers: unknown): layers is Layer[] {
  if (!Array.isArray(layers)) return false;
  if (layers.length === 0) return true;
  const first = layers[0];
  return isRecord(first) && 'kind' in first && !('pixels' in first) && !('meta' in first);
}

const ALL_COLORS: ColorChannelVisibility = { r: true, g: true, b: true };

function readColorVisible(raw: unknown): ColorChannelVisibility {
  if (!isRecord(raw)) return { ...ALL_COLORS };
  return { r: raw.r !== false, g: raw.g !== false, b: raw.b !== false };
}

/** Channel metadata is small, so a bad entry is dropped rather than migrated. */
function readChannels(raw: unknown): AlphaChannel[] {
  if (!Array.isArray(raw)) return [];
  const channels: AlphaChannel[] = [];
  for (const entry of raw) {
    if (!isRecord(entry) || typeof entry.id !== 'string') continue;
    const color = isRecord(entry.color) ? entry.color : null;
    channels.push({
      id: entry.id,
      name: readString(entry.name, 'Alpha'),
      kind: entry.kind === 'quickMask' ? 'quickMask' : 'alpha',
      visible: entry.visible === true,
      color: color
        ? {
            r: readNumber(color.r, CHANNEL_OVERLAY_COLOR.r),
            g: readNumber(color.g, CHANNEL_OVERLAY_COLOR.g),
            b: readNumber(color.b, CHANNEL_OVERLAY_COLOR.b),
            a: readNumber(color.a, CHANNEL_OVERLAY_COLOR.a),
          }
        : { ...CHANNEL_OVERLAY_COLOR },
    });
  }
  return channels;
}

function readChannelData(raw: unknown): Record<string, ArrayBuffer> {
  if (!isRecord(raw)) return {};
  const data: Record<string, ArrayBuffer> = {};
  for (const [id, buffer] of Object.entries(raw)) {
    if (buffer instanceof ArrayBuffer) data[id] = buffer;
  }
  return data;
}

/**
 * An adjustment gains sliders as the editor grows, and a record written before
 * one existed has no value for it. Missing entries fall back to the default, so
 * an old document never reaches the shader with an undefined uniform.
 */
function readAdjustment(raw: AdjustmentState): AdjustmentState {
  const id = raw?.id in ADJUSTMENTS ? raw.id : 'brightnessContrast';
  const defaults = defaultAdjustmentState(id);
  return {
    ...defaults,
    ...raw,
    id,
    values: { ...defaults.values, ...(raw?.values ?? {}) },
  };
}

/** Falls back to a solid black fill when a record is missing or malformed. */
function readFill(raw: FillContent | undefined): FillContent {
  if (!raw || typeof raw !== 'object' || !('kind' in raw)) {
    return { kind: 'solid', color: { r: 0, g: 0, b: 0, a: 1 } };
  }
  if (raw.kind === 'solid') {
    return {
      kind: 'solid',
      color: raw.color ?? { r: 0, g: 0, b: 0, a: 1 },
    };
  }
  if (raw.kind === 'gradient') {
    return {
      kind: 'gradient',
      gradient: raw.gradient ?? BUILTIN_GRADIENTS[0],
      type: raw.type ?? 'linear',
      angle: raw.angle ?? 90,
      scale: raw.scale ?? 100,
      reverse: Boolean(raw.reverse),
    };
  }
  if (raw.kind === 'pattern') {
    const fallback = allPatterns()[0]?.id ?? '';
    return {
      kind: 'pattern',
      patternId: raw.patternId || fallback,
      scale: raw.scale ?? 100,
    };
  }
  return { kind: 'solid', color: { r: 0, g: 0, b: 0, a: 1 } };
}

/**
 * Copies a branch and fills in fields added after the record was written, so a
 * document saved by an older build still satisfies the current layer type.
 */
function readSmartFilters(raw: unknown): SmartFilter[] {
  if (!Array.isArray(raw)) return [];
  const out: SmartFilter[] = [];
  for (const entry of raw) {
    if (!isRecord(entry) || typeof entry.id !== 'string') continue;
    const filter = entry.filter;
    if (typeof filter !== 'string' || !(filter in FILTERS)) continue;
    out.push({
      id: entry.id,
      filter: filter as FilterId,
      values: isRecord(entry.values)
        ? (entry.values as Record<string, number>)
        : {},
      enabled: entry.enabled !== false,
    });
  }
  return out;
}

function readBlendChannels(raw: unknown): BlendChannels {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_BLEND_CHANNELS };
  const entry = raw as Record<string, unknown>;
  return {
    r: entry.r !== false,
    g: entry.g !== false,
    b: entry.b !== false,
    a: entry.a !== false,
  };
}

function readContentOffset(value: unknown): { x: number; y: number } {
  if (
    isRecord(value) &&
    typeof value.x === 'number' &&
    Number.isFinite(value.x) &&
    typeof value.y === 'number' &&
    Number.isFinite(value.y)
  ) {
    return { x: Math.round(value.x), y: Math.round(value.y) };
  }
  return { x: 0, y: 0 };
}

function cloneLayerBranch(layer: Layer): Layer {
  const common = {
    ...layer,
    blendChannels: readBlendChannels(layer.blendChannels),
    blendIf: cloneBlendIf(layer.blendIf),
    lockMask: layer.lockMask === true,
    vectorMask: layer.vectorMask ?? null,
    effects: readLayerEffects(layer.effects),
    contentOffset: readContentOffset(layer.contentOffset),
  };
  if (common.kind === 'group') {
    return { ...common, children: common.children.map(cloneLayerBranch) };
  }
  if (common.kind === 'shape') {
    return {
      ...common,
      geometry: common.geometry ?? { kind: 'freeform', subpaths: [] },
      fill: common.fill ?? { kind: 'none' },
      stroke: common.stroke ?? null,
    };
  }
  if (common.kind === 'text') {
    return {
      ...common,
      content: readString(common.content, 'Text'),
      fontFamily: readString(common.fontFamily, 'Arial'),
      fontSize: typeof common.fontSize === 'number' ? common.fontSize : 48,
      fontWeight: common.fontWeight === 'bold' ? 'bold' : 'normal',
      fontStyle: common.fontStyle === 'italic' ? 'italic' : 'normal',
      align:
        common.align === 'center' || common.align === 'right' ? common.align : 'left',
      color:
        isRecord(common.color) &&
        typeof common.color.r === 'number' &&
        typeof common.color.g === 'number' &&
        typeof common.color.b === 'number'
          ? {
              r: common.color.r,
              g: common.color.g,
              b: common.color.b,
              a: typeof common.color.a === 'number' ? common.color.a : 1,
            }
          : { r: 0, g: 0, b: 0, a: 1 },
      x: typeof common.x === 'number' ? common.x : 0,
      y: typeof common.y === 'number' ? common.y : 0,
    };
  }
  if (common.kind === 'adjustment') {
    return { ...common, adjustment: readAdjustment(common.adjustment) };
  }
  if (common.kind === 'fill') {
    return { ...common, fill: readFill(common.fill) };
  }
  if (common.kind === 'smart') {
    return { ...common, smartFilters: readSmartFilters(common.smartFilters) };
  }
  return common;
}

/** Path metadata is plain JSON, so a malformed entry is dropped rather than fixed. */
function readPaths(raw: unknown): VectorPath[] {
  if (!Array.isArray(raw)) return [];
  const paths: VectorPath[] = [];
  for (const entry of raw) {
    if (!isRecord(entry) || typeof entry.id !== 'string' || !Array.isArray(entry.subpaths)) continue;
    paths.push({
      id: entry.id,
      name: readString(entry.name, 'Path'),
      subpaths: entry.subpaths as VectorPath['subpaths'],
    });
  }
  return paths;
}

/** Same tolerance as paths: a malformed guide is dropped, not repaired. */
function readGuides(raw: unknown): Guide[] {
  if (!Array.isArray(raw)) return [];
  const guides: Guide[] = [];
  for (const entry of raw) {
    if (!isRecord(entry) || typeof entry.id !== 'string') continue;
    if (typeof entry.position !== 'number' || !Number.isFinite(entry.position)) continue;
    if (entry.axis !== 'horizontal' && entry.axis !== 'vertical') continue;
    guides.push({ id: entry.id, axis: entry.axis, position: entry.position });
  }
  return guides;
}

/** Adds v2 raster defaults to a pre-tree flat layer entry. */
export function migrateV1LayerMeta(raw: unknown): RasterLayer | null {
  if (!isRecord(raw)) return null;
  const id = raw.id;
  if (typeof id !== 'string' || id.length === 0) return null;
  return {
    kind: 'raster',
    id,
    name: readString(raw.name, 'Layer'),
    visible: raw.visible !== false,
    opacity: readNumber(raw.opacity, 1),
    blendMode: readBlendMode(raw.blendMode),
    locked: raw.locked === true,
    lockPixels: false,
    lockPosition: false,
    lockMask: false,
    blendChannels: { ...DEFAULT_BLEND_CHANNELS },
    blendIf: {
      channel: DEFAULT_BLEND_IF.channel,
      thisLayer: { ...DEFAULT_BLEND_IF.thisLayer },
      underlying: { ...DEFAULT_BLEND_IF.underlying },
    },
    hasMask: raw.hasMask === true,
    maskEnabled: raw.maskEnabled !== false,
    maskLinked: true,
    clipping: false,
    vectorMask: null,
    effects: null,
    contentOffset: { x: 0, y: 0 },
  };
}

function migrateV1Record(raw: StoredDocumentV1): StoredDocument | null {
  if (!raw.id || typeof raw.width !== 'number' || typeof raw.height !== 'number') return null;
  const layers: Layer[] = [];
  const surfaces: Record<string, StoredSurface> = {};
  for (const entry of raw.layers) {
    if (!isRecord(entry) || !(entry.pixels instanceof ArrayBuffer)) return null;
    const meta = migrateV1LayerMeta(entry.meta);
    if (!meta) return null;
    layers.push(meta);
    surfaces[meta.id] = {
      pixels: entry.pixels,
      mask: entry.mask instanceof ArrayBuffer ? entry.mask : null,
      smartPixels: null,
    };
  }
  return {
    id: raw.id,
    name: readString(raw.name, 'Untitled'),
    width: raw.width,
    height: raw.height,
    activeLayerId: typeof raw.activeLayerId === 'string' ? raw.activeLayerId : null,
    savedAt: readNumber(raw.savedAt, 0),
    compressed: raw.compressed === true,
    schemaVersion: PERSIST_SCHEMA_VERSION,
    layers,
    surfaces,
    channels: [],
    channelData: {},
    colorVisible: { ...ALL_COLORS },
    paths: [],
    activePathId: null,
    guides: [],
  };
}

function normalizeV2Record(raw: Record<string, unknown>): StoredDocument | null {
  const id = raw.id;
  const width = raw.width;
  const height = raw.height;
  if (typeof id !== 'string' || typeof width !== 'number' || typeof height !== 'number') return null;
  if (!looksLikeV2Tree(raw.layers)) return null;
  const surfacesRaw = raw.surfaces;
  if (!isRecord(surfacesRaw)) return null;
  const surfaces: Record<string, StoredSurface> = {};
  for (const [layerId, entry] of Object.entries(surfacesRaw)) {
    if (!isRecord(entry) || !(entry.pixels instanceof ArrayBuffer)) continue;
    surfaces[layerId] = {
      pixels: entry.pixels,
      mask: entry.mask instanceof ArrayBuffer ? entry.mask : null,
      smartPixels: entry.smartPixels instanceof ArrayBuffer ? entry.smartPixels : null,
    };
  }
  return {
    id,
    name: readString(raw.name, 'Untitled'),
    width,
    height,
    activeLayerId: typeof raw.activeLayerId === 'string' ? raw.activeLayerId : null,
    savedAt: readNumber(raw.savedAt, 0),
    compressed: raw.compressed === true,
    schemaVersion: PERSIST_SCHEMA_VERSION,
    layers: raw.layers.map(cloneLayerBranch),
    surfaces,
    // Records written before channels existed simply have none.
    channels: readChannels(raw.channels),
    channelData: readChannelData(raw.channelData),
    colorVisible: readColorVisible(raw.colorVisible),
    paths: readPaths(raw.paths),
    activePathId: typeof raw.activePathId === 'string' ? raw.activePathId : null,
    guides: readGuides(raw.guides),
  };
}

/**
 * Normalises any IndexedDB value to the current schema, or returns null so the
 * caller can skip a corrupt record without taking down the editor.
 */
export function migrateStoredRecord(raw: unknown): StoredDocument | null {
  if (!isRecord(raw)) return null;
  // The shape decides, not `schemaVersion`: a record written by a newer build or
  // truncated mid-write can carry a version its contents do not match.
  if (looksLikeV2Tree(raw.layers) && isRecord(raw.surfaces)) return normalizeV2Record(raw);
  if (looksLikeV1Layers(raw.layers)) return migrateV1Record(raw as unknown as StoredDocumentV1);
  return null;
}

export function buildStoredDocument(params: {
  id: string;
  name: string;
  width: number;
  height: number;
  activeLayerId: string | null;
  layers: Layer[];
  surfaces: Record<string, StoredSurface>;
  channels?: AlphaChannel[];
  channelData?: Record<string, ArrayBuffer>;
  colorVisible?: ColorChannelVisibility;
  paths?: VectorPath[];
  activePathId?: string | null;
  guides?: Guide[];
  compressed: boolean;
  savedAt?: number;
}): StoredDocument {
  return {
    id: params.id,
    name: params.name,
    width: params.width,
    height: params.height,
    activeLayerId: params.activeLayerId,
    savedAt: params.savedAt ?? Date.now(),
    compressed: params.compressed,
    schemaVersion: PERSIST_SCHEMA_VERSION,
    layers: params.layers.map(cloneLayerBranch),
    surfaces: { ...params.surfaces },
    channels: (params.channels ?? []).map((channel) => ({ ...channel, color: { ...channel.color } })),
    channelData: { ...params.channelData },
    colorVisible: { ...(params.colorVisible ?? ALL_COLORS) },
    paths: (params.paths ?? []).map((path) => ({ ...path, subpaths: path.subpaths })),
    activePathId: params.activePathId ?? null,
    guides: (params.guides ?? []).map((guide) => ({ ...guide })),
  };
}

/** Layer tree after migration; surfaces stay in `record.surfaces`. */
export function layersFromStoredRecord(record: StoredDocument): Layer[] {
  return record.layers.map(cloneLayerBranch);
}

let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDatabase(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') {
      resolve(null);
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    // Private-mode Firefox and full disks reject the open; autosave then no-ops.
    request.onerror = () => resolve(null);
    request.onblocked = () => resolve(null);
  });
  return dbPromise;
}

function runTransaction<T>(
  mode: IDBTransactionMode,
  body: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T | null> {
  return openDatabase().then(
    (db) =>
      new Promise<T | null>((resolve) => {
        if (!db) {
          resolve(null);
          return;
        }
        let request: IDBRequest<T>;
        try {
          request = body(db.transaction(STORE, mode).objectStore(STORE));
        } catch {
          resolve(null);
          return;
        }
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => resolve(null);
      }),
  );
}

const canCompress = typeof CompressionStream !== 'undefined';

/** Deflates a pixel buffer; falls back to a plain copy on older browsers. */
export async function packPixels(data: Uint8Array): Promise<ArrayBuffer> {
  const view = new Uint8Array(data);
  if (!canCompress) return view.buffer as ArrayBuffer;
  const stream = new Blob([view]).stream().pipeThrough(new CompressionStream('deflate'));
  return new Response(stream).arrayBuffer();
}

export async function unpackPixels(
  buffer: ArrayBuffer,
  compressed: boolean,
): Promise<Uint8Array> {
  if (!compressed) return new Uint8Array(buffer);
  const stream = new Blob([buffer]).stream().pipeThrough(new DecompressionStream('deflate'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

export function pixelsAreCompressed(): boolean {
  return canCompress;
}

export async function putStoredDocument(record: StoredDocument): Promise<void> {
  await runTransaction('readwrite', (store) => store.put(record));
}

export async function listStoredDocuments(): Promise<StoredDocument[]> {
  const records = await runTransaction<unknown[]>('readonly', (store) =>
    store.getAll() as IDBRequest<unknown[]>,
  );
  if (!records) return [];
  const migrated: StoredDocument[] = [];
  for (const raw of records) {
    const record = migrateStoredRecord(raw);
    if (record) migrated.push(record);
  }
  return migrated.sort((a, b) => a.savedAt - b.savedAt);
}

export async function deleteStoredDocument(id: string): Promise<void> {
  await runTransaction('readwrite', (store) => store.delete(id));
}

export async function clearStoredDocuments(): Promise<void> {
  await runTransaction('readwrite', (store) => store.clear());
}
